// LanguageSwitcher — chọn ngôn ngữ hiển thị.
//
// Dùng 2 chỗ:
//   • Trang Login (variant="pill")  — chọn TRƯỚC khi đăng nhập, lưu localStorage ngay,
//     nên màn login và toàn app sau đó đều đúng ngôn ngữ, không chờ server.
//   • Menu user (variant="menu")    — đổi khi đang dùng, kèm ghi về meta.users.locale.
//
// Nhãn mỗi ngôn ngữ viết BẰNG CHÍNH NGÔN NGỮ ĐÓ ("Tiếng Việt" / "English") — người
// không đọc được ngôn ngữ đang hiển thị vẫn tìm ra ngôn ngữ của mình. Vì vậy nhãn ở
// đây CỐ TÌNH không bọc t(), đừng "sửa" thành chuỗi dịch.
//
// Host tuỳ chọn truyền `onChange` để đồng bộ lên server; kernel đã tự lưu localStorage.

import { useState } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { LOCALES } from './locales.js';
import { setLocale, getLocale, getEnabledLocales } from './runtime.js';

export default function LanguageSwitcher({ variant = 'pill', onChange, className = '' }) {
  const [open, setOpen] = useState(false);
  const [, force] = useState(0);

  const enabled = getEnabledLocales();
  const options = LOCALES.filter((l) => enabled.includes(l.code));
  const current = getLocale();

  // 1 ngôn ngữ thì không có gì để chọn — ẩn hẳn (Core khoá tiếng Việt rơi vào đây).
  if (options.length < 2) return null;

  const pick = async (code) => {
    setOpen(false);
    if (code === current) return;
    await setLocale(code);
    force((n) => n + 1);
    try {
      await onChange?.(code);
    } catch (err) {
      // Ghi về server hụt KHÔNG được rollback giao diện — localStorage đã đổi,
      // user vẫn thấy đúng ngôn ngữ; lần login sau server đồng bộ lại.
      console.warn('[i18n] không lưu được ngôn ngữ lên hồ sơ:', err?.message || err);
    }
  };

  if (variant === 'menu') {
    return (
      <div className={className}>
        {options.map((l) => (
          <button
            key={l.code}
            type="button"
            onClick={() => pick(l.code)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg"
          >
            <span>{l.label}</span>
            {l.code === current ? <Check className="w-4 h-4 text-emerald-600" /> : null}
          </button>
        ))}
      </div>
    );
  }

  const currentMeta = options.find((l) => l.code === current) || options[0];

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white/80 hover:bg-white border border-slate-200 rounded-full shadow-sm transition"
      >
        <Globe className="w-3.5 h-3.5" />
        {currentMeta.label}
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>

      {open ? (
        <div className="absolute right-0 mt-1 w-40 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-50">
          {options.map((l) => (
            <button
              key={l.code}
              type="button"
              onMouseDown={(e) => e.preventDefault()} /* giữ focus để onBlur không đóng trước click */
              onClick={() => pick(l.code)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <span>{l.label}</span>
              {l.code === current ? <Check className="w-4 h-4 text-emerald-600" /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
